'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function LogCalculator() {
  const t = useTranslations("tools.logCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    x: '',
    base: '',
    y: ''
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');
  const [activePreset, setActivePreset] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseValue = (value: string): number | null => {
    if (!value || value.trim() === '') return null;
    const trimmed = value.trim().toLowerCase();
    if (trimmed === 'e') return Math.E;
    const num = parseFloat(trimmed);
    return isNaN(num) ? null : num;
  };

  const formatNumber = (num: number): string => {
    if (Math.abs(num) < 0.0001 || Math.abs(num) > 1000000) {
      return num.toExponential(6);
    }
    return String(Math.round(num * 1000000) / 1000000);
  };

  const handlePresetClick = (baseValue: string) => {
    setInputs({ ...inputs, base: baseValue });
    setActivePreset(baseValue);
    setError('');
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs({ ...inputs, [field]: value });
    setError('');
    if (field === 'base') {
      setActivePreset('');
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const x = parseValue(inputs.x);
    const base = parseValue(inputs.base);
    const y = parseValue(inputs.y);

    let filledCount = 0;
    if (x !== null) filledCount++;
    if (base !== null) filledCount++;
    if (y !== null) filledCount++;

    if (filledCount < 2) {
      setError(t('errorTwoValues'));
      return;
    }

    if (filledCount === 3) {
      setError(t('errorLeaveOneBlank'));
      return;
    }

    try {
      let result: number;
      let resultType: string;
      let formula: string;
      let info: string;

      if (x === null) {
        if (base! <= 0 || base === 1) {
          setError(t('errorBaseInvalid'));
          return;
        }
        result = Math.pow(base!, y!);
        resultType = 'x';
        formula = `x = ${formatNumber(base!)}^${formatNumber(y!)} = ${formatNumber(result)}`;
        info = t('infoCalculateX', { base: formatNumber(base!), y: formatNumber(y!), result: formatNumber(result) });
      } else if (base === null) {
        if (x <= 0) {
          setError(t('errorXPositive'));
          return;
        }
        if (y === 0) {
          setError(t('errorBaseZeroY'));
          return;
        }
        result = Math.pow(x, 1 / y!);
        resultType = 'base';
        formula = `base = ${formatNumber(x)}^(1/${formatNumber(y!)}) = ${formatNumber(result)}`;
        info = t('infoCalculateBase', { x: formatNumber(x), y: formatNumber(y!), result: formatNumber(result) });
      } else {
        if (x <= 0) {
          setError(t('errorXPositive'));
          return;
        }
        if (base <= 0 || base === 1) {
          setError(t('errorBaseInvalid'));
          return;
        }
        result = Math.log(x) / Math.log(base);
        resultType = 'y';
        const baseDisplay = base === Math.E ? 'e' : formatNumber(base);
        formula = `log${base === Math.E ? '' : '₍' + baseDisplay + '₎'}(${formatNumber(x)}) = ${formatNumber(result)}`;
        info = t('infoCalculateY', { base: baseDisplay, result: formatNumber(result), x: formatNumber(x) });
      }

      if (!isFinite(result)) {
        setError(t('errorInvalidResult'));
        return;
      }

      setResults({
        value: formatNumber(result),
        formula,
        info,
        type: resultType
      });

    } catch (err) {
      setError(t('errorCalculation'));
    }
  };

  const handleClear = () => {
    setInputs({ x: '', base: '', y: '' });
    setResults(null);
    setError('');
    setActivePreset('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const
    },
    {},
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const equationDisplayStyle = {
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    borderRadius: '0.75rem',
    padding: '2rem',
    marginBottom: '2rem',
    textAlign: 'center' as const,
    border: '2px solid #cbd5e1',
  };

  const equationFormulaStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      color: '#1e293b',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem',
      flexWrap: 'wrap' as const,
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const inputGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    { fontSize: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const basePresetsStyle = {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem',
    flexWrap: 'wrap' as const,
  };

  const presetBtnStyle = (isActive: boolean) => ({
    padding: '0.4rem 0.8rem',
    background: isActive ? '#2563eb' : '#f1f5f9',
    border: `1px solid ${isActive ? '#2563eb' : '#cbd5e1'}`,
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    transition: 'all 0.2s ease',
    color: isActive ? 'white' : '#475569',
  });

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      padding: '1rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      minHeight: '44px',
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      padding: '1rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      minHeight: '44px',
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #22c55e',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultItemStyle = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
  };

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#475569' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultFormulaStyle = {
    background: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #2563eb',
    marginTop: '1rem',
    fontFamily: "'Courier New', monospace",
    color: '#1e293b',
    fontSize: '0.95rem',
    overflowX: 'auto' as const,
  };

  const errorMessageStyle = {
    background: '#fef2f2',
    border: '2px solid #fecaca',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    fontSize: '0.95rem',
  };

  const infoBoxStyle = {
    background: '#eff6ff',
    borderLeft: '4px solid #2563eb',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1.5rem',
    fontSize: '0.9rem',
    color: '#1e40af',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    color: colors.primary,
    fontWeight: 600,
  };

  const displayBase = inputs.base.trim() || 'b';
  const displayX = inputs.x.trim() || 'x';
  const displayY = inputs.y.trim() || 'y';

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={equationDisplayStyle}>
              <div style={equationFormulaStyle}>
                <span>log<sub style={{ fontSize: '1.2rem', color: '#64748b' }}>{displayBase}</sub></span>
                <span>{displayX}</span>
                <span>=</span>
                <span>{displayY}</span>
              </div>
            </div>

            <div style={inputGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="input-x">{t('labelX')}</label>
                <input
                  style={inputStyle}
                  type="text"
                  id="input-x"
                  placeholder={t('placeholderX')}
                  value={inputs.x}
                  onChange={(e) => handleInputChange('x', e.target.value)}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <div style={{ ...infoBoxStyle, marginTop: '0.5rem', fontSize: '0.85rem' }}>
                  {t('infoXPositive')}
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="input-base">{t('labelBase')}</label>
                <input
                  style={inputStyle}
                  type="text"
                  id="input-base"
                  placeholder={t('placeholderBase')}
                  value={inputs.base}
                  onChange={(e) => handleInputChange('base', e.target.value)}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <div style={basePresetsStyle}>
                  <button
                    type="button"
                    style={presetBtnStyle(activePreset === '10')}
                    onClick={() => handlePresetClick('10')}
                    onMouseEnter={(e) => {
                      if (activePreset !== '10') {
                        e.currentTarget.style.background = '#e2e8f0';
                        e.currentTarget.style.borderColor = '#94a3b8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activePreset !== '10') {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                  >
                    10
                  </button>
                  <button
                    type="button"
                    style={presetBtnStyle(activePreset === 'e')}
                    onClick={() => handlePresetClick('e')}
                    onMouseEnter={(e) => {
                      if (activePreset !== 'e') {
                        e.currentTarget.style.background = '#e2e8f0';
                        e.currentTarget.style.borderColor = '#94a3b8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activePreset !== 'e') {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                  >
                    e
                  </button>
                  <button
                    type="button"
                    style={presetBtnStyle(activePreset === '2')}
                    onClick={() => handlePresetClick('2')}
                    onMouseEnter={(e) => {
                      if (activePreset !== '2') {
                        e.currentTarget.style.background = '#e2e8f0';
                        e.currentTarget.style.borderColor = '#94a3b8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activePreset !== '2') {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                  >
                    2
                  </button>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="input-y">{t('labelY')}</label>
                <input
                  style={inputStyle}
                  type="text"
                  id="input-y"
                  placeholder={t('placeholderY')}
                  value={inputs.y}
                  onChange={(e) => handleInputChange('y', e.target.value)}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </div>

            {error && (
              <div style={errorMessageStyle}>
                {error}
              </div>
            )}

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                }}
              >
                {t('calculateButton')}
              </button>
              <button
                type="button"
                style={clearBtnStyle}
                onClick={handleClear}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                }}
              >
                {t('clearButton')}
              </button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>✨ {t('resultsTitle')}</h3>
              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t('resultAnswer')}</span>
                <span style={resultValueStyle}>{results.value}</span>
              </div>
              <div style={resultFormulaStyle}>{results.formula}</div>
              <div style={infoBoxStyle}>{results.info}</div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('commonBasesTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('base10Title')}</strong> {t('base10Description')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('baseETitle')}</strong> {t('baseEDescription')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('base2Title')}</strong> {t('base2Description')}
          </li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('logRulesTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('productRuleTitle')}</strong> {t('productRuleFormula')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('quotientRuleTitle')}</strong> {t('quotientRuleFormula')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('powerRuleTitle')}</strong> {t('powerRuleFormula')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong style={strongStyle}>{t('changeOfBaseTitle')}</strong> {t('changeOfBaseFormula')}
          </li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('applicationsTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('application1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('application2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('application3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('application4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('application5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faq1Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faq2Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faq3Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faq4Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faq5Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={{ ...listStyle, listStyleType: 'none', paddingLeft: 0 }}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://scientificcalculatoronline.io/how-to-use-log-on-scientific-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://testbook.com/calculators/logarithm-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.calculator.net/log-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://en.wikipedia.org/wiki/Logarithm"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://betterexplained.com/articles/using-logs-in-the-real-world/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}
            >
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}